import { ApiError } from '@sas/form-runtime'
export { ApiError } from '@sas/form-runtime'
import type {
  BootstrapResponse,
  EvaluateResponse,
  ProviderResponse,
  RuntimeFormResponse,
  RuntimeNavigation,
  RuntimeSession,
  SubmissionResponse,
} from './types'
import type { ProviderLoadRequest } from '@sas/form-runtime'

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '')

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, options)
  const text = await response.text()
  let data: unknown = null
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = text
    }
  }
  if (!response.ok) {
    const detail = typeof data === 'object' && data !== null && 'detail' in data ? (data as { detail: unknown }).detail : data || response.statusText
    throw new ApiError(response.status, detail)
  }
  return data as T
}

function runtimeHeaders(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

export const api = {
  bitrixSession(payload: { runtime_key: string; member_id: string; access_token: string; auth_expires: number }) {
    return request<{ runtime_token: string }>('/api/runtime/bitrix-session', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
  },
  bootstrap(ticket: string) {
    return request<BootstrapResponse>('/api/runtime/bootstrap', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket }),
    })
  },

  session(token: string) {
    return request<RuntimeSession>('/api/runtime/session', {
      headers: runtimeHeaders(token),
    })
  },

  navigation(token: string) {
    return request<RuntimeNavigation>('/api/runtime/navigation', {
      headers: runtimeHeaders(token),
    })
  },

  form(token: string, publicKey: string) {
    return request<RuntimeFormResponse>(`/api/runtime/forms/${encodeURIComponent(publicKey)}`, {
      headers: runtimeHeaders(token),
    })
  },

  evaluate(token: string, publicKey: string, values: Record<string, unknown>) {
    return request<EvaluateResponse>(`/api/runtime/forms/${encodeURIComponent(publicKey)}/evaluate`, {
      method: 'POST',
      headers: runtimeHeaders(token),
      body: JSON.stringify({ values }),
    })
  },

  provider(token: string, publicKey: string, providerId: string, payload: ProviderLoadRequest = {}) {
    return request<ProviderResponse>(`/api/runtime/forms/${encodeURIComponent(publicKey)}/providers/${encodeURIComponent(providerId)}`, {
      method: 'POST',
      headers: runtimeHeaders(token),
      body: JSON.stringify({
        values: payload.values || {},
        search: payload.search ?? null,
      }),
    })
  },

  submit(token: string, publicKey: string, values: Record<string, unknown>, revision?: number) {
    return request<SubmissionResponse>(`/api/runtime/forms/${encodeURIComponent(publicKey)}/submit`, {
      method: 'POST',
      headers: runtimeHeaders(token),
      body: JSON.stringify({ values, revision }),
    })
  },
}
